/**
 * Error Parser Utility
 * Created: 2025-11-14
 * Purpose: Parse backend error responses and return user-friendly messages
 * Part of: User Access Management Module - Error Message Enhancement
 */

/**
 * Parses error objects and returns user-friendly error message
 *
 * Handles multiple error formats:
 * 1. JSON string errors from fetch() API
 * 2. Axios error objects
 * 3. Standard Error objects
 * 4. Backend permission denial errors
 *
 * @param error - The error object to parse
 * @returns User-friendly error message string
 */
export function parseErrorMessage(error: unknown): string {
  // Default fallback message
  let errorMessage = 'An unexpected error occurred. Please try again.';

  // Handle Error instances
  if (error instanceof Error) {
    try {
      // Try to parse as JSON (from fetch() API responses)
      const errorData = JSON.parse(error.message);

      // Handle nested detail object (backend permission errors)
      // Example: {"detail": {"error": "Insufficient permissions", "message": "You need..."}}
      if (errorData.detail && typeof errorData.detail === 'object') {
        // Check if it's a permission error - replace with generic message
        if (errorData.detail.error === 'Insufficient permissions' ||
            errorData.detail.required_permission) {
          errorMessage = 'Permission denied. You do not have access to perform this action.';
        } else {
          errorMessage = errorData.detail.message || 'Permission denied. You do not have access to perform this action.';
        }
      }
      // Handle detail as string
      // Example: {"detail": "Resource not found"}
      else if (errorData.detail && typeof errorData.detail === 'string') {
        errorMessage = errorData.detail;
      }
      // Handle direct message property
      // Example: {"message": "Validation failed"}
      else if (errorData.message) {
        errorMessage = errorData.message;
      }
      // Use original error message if parsing succeeded but no recognized format
      else {
        errorMessage = error.message;
      }
    } catch {
      // Not JSON, use the error message as-is
      errorMessage = error.message;
    }
  }
  // Handle axios error objects
  else if (typeof error === 'object' && error !== null) {
    const axiosError = error as any;

    // Check for axios error response
    if (axiosError.response?.data) {
      const responseData = axiosError.response.data;

      // Handle nested detail object
      if (responseData.detail && typeof responseData.detail === 'object') {
        // Check if it's a permission error - replace with generic message
        if (responseData.detail.error === 'Insufficient permissions' ||
            responseData.detail.required_permission) {
          errorMessage = 'Permission denied. You do not have access to perform this action.';
        } else {
          errorMessage = responseData.detail.message || 'Permission denied. You do not have access to perform this action.';
        }
      }
      // Handle detail as string
      else if (responseData.detail && typeof responseData.detail === 'string') {
        errorMessage = responseData.detail;
      }
      // Handle direct message
      else if (responseData.message) {
        errorMessage = responseData.message;
      }
    }
    // Check for error message property
    else if (axiosError.message) {
      errorMessage = axiosError.message;
    }
  }

  return errorMessage;
}

/**
 * Creates a user-friendly error message for API token operations
 * @param error - The error object
 * @param operation - The operation being performed (e.g., 'generate', 'delete', 'refresh')
 * @returns Formatted error message with operation context
 */
export function formatApiTokenError(error: unknown, operation: string): string {
  const baseMessage = parseErrorMessage(error);

  // Map operation to user-friendly verb
  const operationMap: Record<string, string> = {
    generate: 'generate',
    create: 'create',
    delete: 'revoke',
    revoke: 'revoke',
    refresh: 'refresh',
    list: 'load'
  };

  const verb = operationMap[operation.toLowerCase()] || operation;

  return `Failed to ${verb} API token: ${baseMessage}`;
}

/**
 * Creates a user-friendly error message for settings operations
 * @param error - The error object
 * @param setting - The setting being modified (e.g., 'account', 'co-pilot', 'MCP')
 * @returns Formatted error message with setting context
 */
export function formatSettingsError(error: unknown, setting: string): string {
  const baseMessage = parseErrorMessage(error);
  return `Failed to update ${setting} settings: ${baseMessage}`;
}
